import { useEffect } from "react";
import gql from "graphql-tag";
import { useQuery as defaultUseQuery } from "@apollo/react-hooks";
import { MockedResponse, ResultFunction } from "@apollo/react-testing";
import { useCurrentAccountId } from "@zapier/payment/esm/hooks/useCurrentAccountId";
import {
  ApolloInjection,
  currencyRulesetQuery as currencyRulesetQueryData,
  FetchResult,
} from "@zapier/payment/esm/types";
import { logToSentry } from "@zapier/payment/esm/utils";
import { GraphQLError } from "graphql";

type Input = {
  accountId?: number | undefined;
} & ApolloInjection;

const currencyRulesetQuery = gql`
  query currencyRulesetQuery($accountId: Int!) {
    account(id: $accountId) {
      id
      currencyRuleset {
        currency
        method
        processor
        submethod
        submethodDisplay
      }
    }
  }
`;

function useCurrencyRuleset(props: Input = {}) {
  const currentAccount = useCurrentAccountId(props);
  const useQuery = props?.useQuery || defaultUseQuery;
  const accountId = props.accountId || currentAccount?.accountId;

  const { data, error, loading } = useQuery<currencyRulesetQueryData>(
    currencyRulesetQuery,
    {
      skip: !accountId,
      variables: { accountId },
    }
  );

  useEffect(() => {
    if (error) {
      logToSentry("currencyRuleset.load", error);
    }
  }, [error]);

  return {
    error: error ? "There was an issue loading data." : undefined,
    isLoading: loading,
    currencyRuleset: data?.account?.currencyRuleset ?? [],
  };
}

type Result = FetchResult<currencyRulesetQueryData> & {
  // Note, I would have added this to the FetchResult type in types.ts, but it messed up a bunch of types elsewhere 🤷‍♂️
  errors?: ReadonlyArray<GraphQLError>;
};

const currencyRulesetQueryMock = (
  accountId = 1,
  overrides?: Omit<MockedResponse, "request"> & {
    result?: Result | ResultFunction<Result>;
  }
): MockedResponse => {
  const request = { query: currencyRulesetQuery, variables: { accountId } };

  if (overrides) {
    return { request, ...overrides };
  }

  return {
    request,
    result: {
      data: {
        account: {
          id: accountId,
          currencyRuleset: [
            {
              currency: "USD",
              method: "card",
              processor: "stripe",
              submethod: "visa",
              submethodDisplay: "Visa",
            },
          ],
        },
      },
    },
  };
};

export { currencyRulesetQuery, useCurrencyRuleset, currencyRulesetQueryMock };
